import {Button, Group, Modal, Stack} from '@mantine/core';
import React, {useState} from 'react';

import type {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {
  areAllHeadersMapped,
  convertSpreadsheetRowsToProteinCreationRows,
  getHeaderMappingsByName,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationSpreadsheetImportUtil';
import type {ProteinCreationRow} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import {parseProteinCreationSpreadsheet} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportDAO';
import {
  NdcSpreadsheetComponentFormatType,
  ProteinCreationSpreadsheetParseResponse,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import UploadSpreadsheetPane from 'antibodies/nested-data-creation/spreadsheet-import/panes/UploadSpreadsheetPane';
import type {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import ColumnMapper from 'spreadsheet-import-wizard/shared/ColumnMapper';
import type {MappableField} from 'spreadsheet-import-wizard/shared/ColumnMapper';
import ModalFooter from 'ui-library/modals/ModalFooter';
import {getUserMessage, isServerRequestError} from 'util/Net';

const DEFAULT_PARSE_ERROR = 'We could not read this spreadsheet. Check the file and try again.';

enum ImportStep {
  SELECT_SPREADSHEET = 'SELECT_SPREADSHEET',
  MAP_COLUMNS = 'MAP_COLUMNS',
}

interface ProteinCreationSpreadsheetImportModalProps {
  columns: ReadonlyArray<StructuredTableColumn>;
  format: AntibodyFormat;
  onClose: () => void;
  onImportRows: (rows: Array<ProteinCreationRow>) => void;
  startingType: NdcSpreadsheetComponentFormatType;
}

export default function ProteinCreationSpreadsheetImportModal({
  columns,
  format,
  onClose,
  onImportRows,
  startingType,
}: ProteinCreationSpreadsheetImportModalProps): JSX.Element {
  const [step, setStep] = useState(ImportStep.SELECT_SPREADSHEET);
  const [spreadsheetFile, setSpreadsheetFile] = useState<File | null>(null);
  const [parsedSpreadsheet, setParsedSpreadsheet] = useState<ProteinCreationSpreadsheetParseResponse | null>(
    null
  );
  const [parseError, setParseError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [mappings, setMappings] = useState<Record<string, string>>({});

  const resetParsedSpreadsheet = (): void => {
    setParsedSpreadsheet(null);
    setParseError(null);
    setMappings({});
  };

  const clearSpreadsheet = (): void => {
    setSpreadsheetFile(null);
    resetParsedSpreadsheet();
  };

  const handleSetSpreadsheetURL = async (spreadsheetURL: string): Promise<void> => {
    setIsParsing(true);
    setParseError(null);
    try {
      const parsed = await parseProteinCreationSpreadsheet({spreadsheetLocation: spreadsheetURL});
      setParsedSpreadsheet(parsed);
      setMappings(getHeaderMappingsByName(parsed.headers, columns));
    } catch (error) {
      setParseError(getUserMessage(error) ?? DEFAULT_PARSE_ERROR);
      if (!isServerRequestError(error)) {
        throw error;
      }
    } finally {
      setIsParsing(false);
    }
  };

  const canUpload = parsedSpreadsheet != null && areAllHeadersMapped(parsedSpreadsheet.headers, mappings);

  const handleUpload = (): void => {
    if (parsedSpreadsheet == null || !canUpload) {
      return;
    }
    onImportRows(
      convertSpreadsheetRowsToProteinCreationRows({columns, mappings, rows: parsedSpreadsheet.rows})
    );
    onClose();
  };

  const fields: Array<MappableField> = columns.map((column) => ({
    id: column.provenanceUuid,
    displayName: column.name,
  }));

  return (
    <Modal
      title="Add from spreadsheet"
      opened={true}
      onClose={onClose}
      size="xl"
      closeButtonProps={{'aria-label': 'Close'}}
      lockScroll={false}
    >
      <Stack spacing="lg">
        {step === ImportStep.MAP_COLUMNS && parsedSpreadsheet != null ? (
          <ColumnMapper
            headers={parsedSpreadsheet.headers}
            fields={fields}
            mappings={mappings}
            onMappingChange={setMappings}
            sampleValuesByHeader={parsedSpreadsheet.sampleValuesByHeader}
            sourceHeadingLabel="Spreadsheet column"
            targetHeadingLabel="Column type"
          />
        ) : (
          <UploadSpreadsheetPane
            startingType={startingType}
            spreadsheetFile={spreadsheetFile}
            setSpreadsheetFile={(file) => {
              setSpreadsheetFile(file);
              resetParsedSpreadsheet();
            }}
            setSpreadsheetURL={handleSetSpreadsheetURL}
            clearSpreadsheet={clearSpreadsheet}
            columnInferenceErrorMessages={parseError != null ? [parseError] : []}
            isInferringColumns={isParsing}
            selectedAntibodyFormat={format}
          />
        )}
      </Stack>
      <ModalFooter>
        <Group position="apart">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {step === ImportStep.MAP_COLUMNS ? (
            <Group spacing="sm">
              <Button
                variant="secondary"
                onClick={() => {
                  setStep(ImportStep.SELECT_SPREADSHEET);
                }}
              >
                Back
              </Button>
              <Button disabled={!canUpload} onClick={handleUpload}>
                Upload
              </Button>
            </Group>
          ) : (
            <Button
              disabled={parsedSpreadsheet == null || isParsing}
              onClick={() => {
                setStep(ImportStep.MAP_COLUMNS);
              }}
            >
              Next
            </Button>
          )}
        </Group>
      </ModalFooter>
    </Modal>
  );
}
